import { useState, useEffect } from 'react';
import type React from 'react';
import { useToast } from '@podus/ui';
import { api, BASE_URL } from '@/lib/api';

interface UploadLogoCardProps {
    initialLogo: string | null;
}

export default function UploadLogoCard({ initialLogo }: UploadLogoCardProps) {
    const toast = useToast();
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (initialLogo) {
            setLogoPreview(`${BASE_URL}/api/${initialLogo}`);
        }
    }, [initialLogo]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setLogoPreview(reader.result as string);
            reader.readAsDataURL(file);

            setLoading(true);
            const formData = new FormData();
            formData.append('logo', file);
            try {
                await api.upload('/api/settings/logo', formData);
                toast.success('Logo updated successfully');
            } catch (err) {
                toast.error(err instanceof Error ? err.message : 'Failed to upload logo');
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="rounded-xl border border-[#eee] bg-white p-6">
            <h3 className="text-[16px] font-bold text-secondary">Upload Organisational Logo</h3>

            <div className="mt-6 flex justify-center">
                <div className="flex h-40 w-40 items-center justify-center overflow-hidden rounded-xl border border-[#eee] bg-[#fafafa]">
                    {logoPreview ? (
                        <img src={logoPreview} alt="Organisation logo" className="h-full w-full object-contain p-2" />
                    ) : (
                        <div className="text-center text-[13px] text-[#aaa]">No logo uploaded</div>
                    )}
                </div>
            </div>

            <div className="mt-6 flex justify-center">
                <label
                    className={`cursor-pointer rounded-lg bg-secondary px-8 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-secondary-400 ${loading ? 'opacity-50' : ''}`}
                >
                    {loading ? 'Uploading...' : 'Upload Logo'}
                    <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileChange}
                        disabled={loading}
                    />
                </label>
            </div>
        </div>
    );
}
